<script lang="ts" setup>
import {
  CONTEST_STATUSES,
  computed,
  navigateTo,
  onUnmounted,
  ref,
} from '#imports';
import lottie from 'lottie-web';
import launchingRocketJson from '~/assets/lottie/launching-rocket.json';
import UserHoverCard from '~/components/UserHoverCard/UserHoverCard.vue';

const props = defineProps<{
  contestId?: number;
  title?: string;
  status?: number;
  registered?: boolean;
  typeDesc?: {
    id?: number;
    name?: string;
  };
  matchTypeDesc?: {
    id?: number;
    name?: string;
  };
  startTime?: number;
  endTime?: number;
  ownerVo?: {
    avatar?: string;
    uid?: string;
    nickname?: string;
  };
  avatarList?: string[]; //头像列表
  count?: number; //报名人数
}>();

const countText = computed(() => {
  const count = Number(props.count) || 0;
  if (count < 50) return '开放报名中';
  const formatCount = (num: number) =>
    num < 1000 ? num.toString() : `${(num / 1000).toFixed(1)}K`;
  return `${formatCount(count)}人已报名`;
});

const timeBeforeStartText = computed(() => {
  const now = Date.now();
  const isStarted = now >= (props.startTime as number);
  const targetTime = isStarted ? props.endTime : props.startTime;
  const delta = (targetTime as number) - now;
  if (delta < 0) return isStarted ? '已结束' : '已开始';

  const preciseDays = delta / (1000 * 60 * 60 * 24);
  const days = Math.floor(preciseDays);
  const hours = Math.floor((delta % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60));
  const minutes = Math.floor((delta % (1000 * 60 * 60)) / (1000 * 60));

  const prefix = `距${isStarted ? '结束' : '开始'}`;
  if (preciseDays > 7) return `${prefix} ${days}天`;

  const timeParts = [];
  if (days > 0) timeParts.push(`${days}天`);
  if (hours > 0 || days > 0) timeParts.push(`${hours}时`);
  timeParts.push(`${minutes}分`);

  return `${prefix} ${timeParts.join('')}`;
});

const handleClick = () => {
  navigateTo({
    name: 'oj_contest_detail',
    query: {
      cid: props.contestId,
    },
  });
};

// 定义动画容器和动画实例
const animationContainer = ref<HTMLElement | null>(null);
const animationInstance = ref<any>(null);

// 是否显示动画
const showAnimation = ref(false);

// 鼠标悬停时显示动画
const handleMouseEnter = () => {
  if (props.registered && props.status === CONTEST_STATUSES.IN_PROGRESS) {
    showAnimation.value = true;

    // 确保容器存在
    if (animationContainer.value) {
      // 加载 Lottie 动画
      animationInstance.value = lottie.loadAnimation({
        container: animationContainer.value,
        renderer: 'svg',
        loop: true,
        autoplay: true,
        animationData: launchingRocketJson,
      });
    }
  }
};

// 鼠标离开时销毁动画
const handleMouseLeave = () => {
  showAnimation.value = false;

  // 销毁动画
  if (animationInstance.value) {
    animationInstance.value.destroy();
    animationInstance.value = null;
  }
};

// 组件卸载时确保动画被销毁
onUnmounted(() => {
  if (animationInstance.value) {
    animationInstance.value.destroy();
    animationInstance.value = null;
  }
});
</script>

<template>
  <li
    class="relative w-[266px] cursor-pointer rounded border border-solid border-[rgba(0,0,0,.06)] px-3 py-4 text-xs leading-5 text-font3 !transition-colors !duration-500 hover:border-colorText"
    :class="{ 'animate': showAnimation }"
    @click="handleClick"
    @mouseenter="handleMouseEnter"
    @mouseleave="handleMouseLeave"
  >
    <!-- Lottie 动画容器 -->

    <transition name="fade">
      <div
        v-show="showAnimation"
        class="absolute inset-0 left-0 -top-[1px] -right-[1px] bottom-0 z-10  bg-colorTextTertiary rounded"
      >
        <div
        ref="animationContainer"
        class="absolute -bottom-[8px] left-1/2 w-[282px] -translate-x-1/2 overflow-hidden"
      ></div>
      <div class="absolute text-sm bottom-[10px] left-1/2 w-[80px] h-8 flex-center -translate-x-1/2 bg-primary rounded text-white font-bold">立即比赛</div>
    </div>
    </transition>

    <div class="content w-full">
      <!-- 标题 -->
      <a-tooltip placement="top">
        <template #title>{{ title }}</template>
        <h6
          class="block max-w-full overflow-hidden text-ellipsis whitespace-nowrap text-sm leading-[22px] font-bold text-font1"
        >
          {{ title }}
        </h6>
      </a-tooltip>
      <!-- 标题 end-->

      <!-- 标签 -->
      <div class="tag mt-2 flex items-center gap-1.5">
        <span
          v-if="status === CONTEST_STATUSES.UNSTART"
          class="!bg-[#FF7D00] !text-white"
          >报名中</span
        >
        <span
          v-else-if="status === CONTEST_STATUSES.IN_PROGRESS"
          class="!bg-[#F53F3F] !text-white"
          >比赛中</span
        >
        <span>{{ typeDesc?.name }}</span>
        <span>{{ matchTypeDesc?.name }}</span>
      </div>
      <!-- 标签 end-->

      <div class="mt-1.5 !text-xs leading-[14px] flex items-center text-font2">
        {{ timeBeforeStartText }}
      </div>

      <div
        class="mt-2 flex items-center border-t border-solid border-colorBorderSecondary pt-[9px]"
      >
        <div class="text-xs leading-[14px]">主持人</div>

        <UserHoverCard :uid="ownerVo?.uid ?? ''">
          <NuxtLink
            class="flex items-center"
            :to="{
              name: 'oj_home',
              query: {
                id: ownerVo?.uid,
              },
            }"
            @click.stop
            ><a-avatar
              class="!mr-1 border-solid !border-[#ededed]"
              :size="16"
              :src="ownerVo?.avatar"
            ></a-avatar>
            <span
              class="max-w-[58px] text-xs leading-[14px] overflow-hidden text-ellipsis whitespace-nowrap text-font3"
              >{{ ownerVo?.nickname }}</span
            >
          </NuxtLink>
        </UserHoverCard>

        <div class="ml-auto text-xs leading-[14px] flex items-center">
          <a-avatar
            v-for="(avatar, index) in avatarList"
            :key="index"
            class="u-avatar border-solid !border-[#ededed] !bg-white"
            :size="16"
            :src="avatar"
          ></a-avatar>

          <span class="ml-1">{{ countText }}</span>
        </div>
      </div>
    </div>
    <span
      v-if="registered"
      class="absolute right-[-1px] top-[-1px] rounded-bl rounded-tr bg-primary px-[6px] text-xs leading-5 text-white"
      >已报名</span
    >
  </li>
</template>

<style scoped>
.tag {
  span {
    @apply flex-center h-5 flex-shrink-0 rounded-sm bg-[#f3f3f3] px-[8px] text-xs text-font2;
  }
}

.u-avatar {
  @apply ml-[-8px] inline-flex first:ml-0;
}
.animate {
  @apply border-transparent
}
.animate .content {
  filter: blur(2px);
}
.fade-enter-active {
  transition: opacity 0.3s ease;
}
.fade-leave-active {
  transition: opacity 1s ease;
}

.fade-enter-from,
.fade-leave-to {
  opacity: 0;
}
</style>
